import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Briefcase, Phone, MapPin, Clock, DollarSign, Lock, CheckCircle2, Trash2, X, Star } from "lucide-react";
import TierBadge, { TierBadgePill } from "../components/TierBadge";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG   = "#F8F9FA";
const DARK = "#1A1A2E";
const GREEN = "#1A7A4A";
const RED  = "#A32D2D";
const GREY = "#5a5d66";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  scheduled: { label: "SCHEDULED", color: GOLD },
  in_progress: { label: "IN PROGRESS", color: "#1e6fb8" },
  completed: { label: "COMPLETED", color: GREEN },
  cancelled: { label: "CANCELLED", color: RED },
};

const JobsToday = () => {
  // Contractor identity — for now we let the user paste their contractor ID
  // (in production this would come from auth context).
  const [contractorId, setContractorId] = useState(
    () => localStorage.getItem("nearnow_contractor_id") || ""
  );
  const [jobs, setJobs] = useState([]);
  const [plan, setPlan] = useState(null);
  const [businessName, setBusinessName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [reviewJob, setReviewJob] = useState(null);

  const fetchJobs = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/contractor/jobs-today`, { params: { contractor_id: contractorId } });
      setJobs(r.data.jobs || []);
      setPlan(r.data.plan || null);
      setBusinessName(r.data.business_name || null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "tier_required") {
        setError({ kind: "tier", message: d.message, current: d.current_plan });
      } else if (e?.response?.status === 404) {
        setError({ kind: "not_found", message: "Contractor not found. Check the ID and try again." });
      } else {
        setError({ kind: "generic", message: e?.message || "Could not load jobs." });
      }
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const totalValue = jobs.reduce((sum, j) => sum + (j.estimated_value_cents || 0), 0);
  const completed = jobs.filter((j) => j.status === "completed").length;

  const saveId = (id) => {
    const v = (id || "").trim();
    if (v) localStorage.setItem("nearnow_contractor_id", v);
    setContractorId(v);
  };

  return (
    <div data-testid="jobs-today-page" style={{ background: BG, minHeight: "100vh" }}>
      {/* Hero */}
      <section style={{
        background: `linear-gradient(180deg, ${NAVY} 0%, #060d1a 100%)`,
        color: "#fff", padding: "28px 16px 22px",
        borderBottom: `3px solid ${GOLD}`,
      }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <p style={{ color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 4, margin: 0, fontSize: 12 }}>
            PRO TIER · CONTRACTOR DASHBOARD
          </p>
          <h1 style={{
            fontFamily: "Bebas Neue, sans-serif",
            fontSize: "clamp(32px, 8vw, 56px)",
            letterSpacing: 1, margin: "4px 0 4px", lineHeight: 1.05,
          }}>
            Jobs Today
          </h1>
          <p style={{ color: "#cbd0db", fontSize: 15, margin: 0 }}>
            Log every job. Track every dollar. Never lose track of what&apos;s on the schedule.
          </p>
          {plan && (
            <div style={{ marginTop: 12 }} data-testid="jobs-today-tier-header">
              <TierBadgePill tier={plan} size="sm"
                             style={{ background: "rgba(255,255,255,0.94)", color: NAVY }}
                             testid={`dashboard-tier-pill-${plan}`} />
              {businessName && (
                <span style={{ marginLeft: 10, color: "#cbd0db", fontSize: 13, letterSpacing: 1 }}>
                  {businessName.toUpperCase()}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Identity bar */}
      <section style={{ maxWidth: 980, margin: "0 auto", padding: "18px 16px 0" }}>
        <div data-testid="contractor-id-bar" style={{
          background: "#fff", borderRadius: 12, padding: 14,
          border: `1px solid ${GOLD}33`, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
        }}>
          <Briefcase color={NAVY} size={20} />
          <label className="nn-label" style={{ color: NAVY, fontSize: 13 }}>CONTRACTOR ID</label>
          <input
            data-testid="contractor-id-input"
            className="nn-input"
            style={{ flex: 1, minWidth: 200, minHeight: 48 }}
            placeholder="paste your contractor ID"
            value={contractorId}
            onChange={(e) => setContractorId(e.target.value)}
            onBlur={(e) => saveId(e.target.value)}
          />
          <button
            data-testid="reload-jobs"
            onClick={fetchJobs}
            disabled={!contractorId}
            className="btn-gold tap"
            style={{ minHeight: 48 }}
          >
            LOAD JOBS
          </button>
        </div>
      </section>

      {/* Error / Tier gate */}
      <AnimatePresence>
        {error && (
          <motion.section
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{ maxWidth: 980, margin: "12px auto 0", padding: "0 16px" }}
          >
            {error.kind === "tier" ? (
              <div data-testid="tier-gate" style={{
                background: NAVY, color: "#fff", borderRadius: 14, padding: 22,
                borderLeft: `4px solid ${GOLD}`, display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap",
              }}>
                <Lock color={GOLD} size={28} />
                <div style={{ flex: 1, minWidth: 240 }}>
                  <p style={{ color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 3, fontSize: 12, margin: 0 }}>
                    PRO TIER ONLY
                  </p>
                  <h3 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 28, margin: "4px 0 8px", letterSpacing: 1 }}>
                    Jobs Today is a Pro Tier feature.
                  </h3>
                  <p style={{ color: "#cbd0db", margin: 0 }}>
                    {error.message} Your current plan: <strong>{error.current || "—"}</strong>.
                  </p>
                </div>
                <Link to="/pricing" data-testid="upgrade-cta" className="btn-gold tap"
                  style={{ minHeight: 56, display: "inline-flex", alignItems: "center", padding: "0 22px" }}>
                  UPGRADE TO PRO
                </Link>
              </div>
            ) : (
              <div data-testid="jobs-error" style={{
                background: "#fff", border: `1px solid ${RED}66`, borderLeft: `4px solid ${RED}`,
                color: RED, padding: 16, borderRadius: 10,
              }}>
                {error.message}
              </div>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* Summary + Add */}
      {!error?.kind && contractorId && (
        <section style={{ maxWidth: 980, margin: "16px auto 0", padding: "0 16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            <Stat label="Jobs Today"   value={jobs.length}   testid="stat-count"     icon={<Briefcase size={18} />} />
            <Stat label="Completed"    value={completed}     testid="stat-completed" icon={<CheckCircle2 size={18} />} color={GREEN} />
            <Stat label="Est. Revenue" value={`$${(totalValue / 100).toLocaleString()}`} testid="stat-revenue" icon={<DollarSign size={18} />} color={GOLD} />
          </div>
          <button
            data-testid="add-job-btn"
            onClick={() => setShowForm(true)}
            className="btn-gold tap"
            style={{ marginTop: 14, width: "100%", minHeight: 60, fontSize: 17,
                     display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10 }}
          >
            <Plus size={22} /> ADD A JOB FOR TODAY
          </button>
        </section>
      )}

      {/* Jobs list */}
      {!error?.kind && contractorId && (
        <section style={{ maxWidth: 980, margin: "18px auto 80px", padding: "0 16px" }}>
          {loading && <p data-testid="jobs-loading" style={{ color: "#5a5d96", textAlign: "center" }}>Loading…</p>}
          {!loading && jobs.length === 0 && (
            <div data-testid="jobs-empty" style={{
              background: "#fff", border: `2px dashed ${GOLD}55`, padding: 28, borderRadius: 14,
              textAlign: "center", color: "#5a5d96",
            }}>
              <Briefcase color={GOLD} size={36} />
              <p style={{ margin: "10px 0 4px", color: NAVY, fontSize: 18, fontWeight: 600 }}>
                No jobs on the board yet.
              </p>
              <p style={{ margin: 0, fontSize: 14 }}>
                Add your first job to start tracking today's schedule.
              </p>
            </div>
          )}
          <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
            {jobs.map((j) => <JobCard key={j.id} job={j} onChanged={fetchJobs}
              onLogReview={(job) => setReviewJob(job)} />)}
          </div>
        </section>
      )}

      {/* Add form modal */}
      <AnimatePresence>
        {showForm && (
          <JobForm
            contractorId={contractorId}
            onClose={() => setShowForm(false)}
            onCreated={() => { setShowForm(false); fetchJobs(); }}
          />
        )}
      </AnimatePresence>

      {/* Log Review Won modal */}
      <AnimatePresence>
        {reviewJob && (
          <ReviewWinForm
            contractorId={contractorId}
            job={reviewJob}
            onClose={() => setReviewJob(null)}
            onCreated={() => { setReviewJob(null); fetchJobs(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const Stat = ({ label, value, testid, icon, color = NAVY }) => (
  <div data-testid={testid} style={{
    background: "#fff", padding: 14, borderRadius: 12,
    border: `1px solid ${GOLD}22`, display: "flex", alignItems: "center", gap: 10,
  }}>
    <div style={{ color }}>{icon}</div>
    <div>
      <div style={{ color: "#5a5d96", fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color, fontFamily: "Bebas Neue, sans-serif", fontSize: 26, letterSpacing: 1, lineHeight: 1 }}>{value}</div>
    </div>
  </div>
);

const JobCard = ({ job, onChanged, onLogReview }) => {
  const cfg = STATUS_LABEL[job.status] || STATUS_LABEL.scheduled;
  const advance = async (next) => {
    try {
      await axios.patch(`${API}/contractor/jobs-today/${job.id}`, { status: next });
      onChanged();
    } catch (e) { /* swallow */ }
  };
  const archive = async () => {
    if (!confirm("Archive this job?")) return;
    try {
      await axios.patch(`${API}/contractor/jobs-today/${job.id}`, { archived: true });
      onChanged();
    } catch (e) { /* swallow */ }
  };
  return (
    <motion.div
      data-testid={`job-card-${job.id}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: "#fff", borderRadius: 12, padding: 16,
        borderLeft: `4px solid ${cfg.color}`, boxShadow: "0 6px 16px rgba(10,22,40,0.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <span style={{
            background: `${cfg.color}22`, color: cfg.color, fontSize: 11,
            padding: "3px 8px", borderRadius: 4, letterSpacing: 1, fontFamily: "Bebas Neue, sans-serif",
          }}>
            {cfg.label}
          </span>
          <h3 style={{ margin: "8px 0 6px", color: NAVY, fontSize: 18 }}>{job.title}</h3>
          <div style={{ display: "grid", gap: 4, color: "#5a5d96", fontSize: 14 }}>
            {job.customer_name && <Row icon={<Briefcase size={14} />} text={job.customer_name} />}
            {job.customer_phone && <Row icon={<Phone size={14} />} text={job.customer_phone} />}
            {job.address && <Row icon={<MapPin size={14} />} text={job.address} />}
            {job.scheduled_time && <Row icon={<Clock size={14} />} text={job.scheduled_time} />}
            {!!job.estimated_value_cents && (
              <Row icon={<DollarSign size={14} />} text={`$${(job.estimated_value_cents / 100).toLocaleString()} est.`} bold color={GOLD} />
            )}
          </div>
          {job.notes && (
            <p style={{ marginTop: 8, color: DARK, fontSize: 13, background: "#F8F9FA",
                       padding: 8, borderRadius: 6, borderLeft: `2px solid ${GOLD}` }}>
              {job.notes}
            </p>
          )}
          {(job.estimated_value_cents || 0) >= 500000 && (
            <div data-testid={`review-badge-${job.id}`} style={{
              marginTop: 10, padding: "8px 10px", borderRadius: 6,
              background: "rgba(212,175,55,0.10)", border: `1px solid ${GOLD}66`,
              display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: NAVY,
            }}>
              <CheckCircle2 size={14} color={GOLD} />
              <span>
                <strong>Hermes will follow up.</strong> In 2 days you'll get an email
                with a copy-paste review request to send {job.customer_name || "the customer"}.
              </span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 130 }}>
          {job.status === "scheduled" && (
            <button data-testid={`start-${job.id}`} onClick={() => advance("in_progress")}
              className="tap" style={smallBtn(NAVY)}>START</button>
          )}
          {job.status === "in_progress" && (
            <button data-testid={`complete-${job.id}`} onClick={() => advance("completed")}
              className="tap" style={smallBtn(GREEN)}>COMPLETE</button>
          )}
          {(job.status === "scheduled" || job.status === "in_progress") && (
            <button data-testid={`cancel-${job.id}`} onClick={() => advance("cancelled")}
              className="tap" style={smallBtn(RED, true)}>CANCEL</button>
          )}
          <button data-testid={`archive-${job.id}`} onClick={archive}
            className="tap" style={{ ...smallBtn("#5a5d96", true), display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
            <Trash2 size={14} /> ARCHIVE
          </button>
          {job.status === "completed" && onLogReview && (
            <button data-testid={`log-review-${job.id}`}
              onClick={() => onLogReview(job)}
              className="tap"
              style={{ ...smallBtn("#1A7A4A", true), display: "inline-flex",
                       alignItems: "center", gap: 6, justifyContent: "center" }}>
              ⭐ LOG REVIEW WON
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Row = ({ icon, text, color, bold }) => (
  <div style={{ display: "flex", gap: 6, alignItems: "center", color: color || "#5a5d96",
                fontWeight: bold ? 700 : 400 }}>
    {icon} <span>{text}</span>
  </div>
);

const smallBtn = (color, outline = false) => ({
  background: outline ? "transparent" : color,
  color: outline ? color : "#fff",
  border: `1px solid ${color}`,
  borderRadius: 6,
  padding: "10px 12px",
  fontFamily: "Bebas Neue, sans-serif",
  letterSpacing: 1.5, fontSize: 13,
  cursor: "pointer", minHeight: 44,
});

const JobForm = ({ contractorId, onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [address, setAddress] = useState("");
  const [time, setTime] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!title.trim()) { setErr("Title required"); return; }
    setSubmitting(true); setErr(null);
    try {
      await axios.post(`${API}/contractor/jobs-today`, {
        contractor_id: contractorId,
        title, customer_name: customerName, customer_phone: customerPhone,
        address, scheduled_time: time, notes,
        estimated_value_cents: value ? Math.round(parseFloat(value) * 100) : 0,
      });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.message || "Could not save job");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      data-testid="job-form-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, background: "rgba(10,22,40,0.65)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 100, padding: 0,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
        transition={{ type: "spring", stiffness: 280, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", width: "100%", maxWidth: 560,
          borderRadius: "20px 20px 0 0",
          padding: "20px 18px 28px",
          maxHeight: "92vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontFamily: "Bebas Neue, sans-serif", fontSize: 28, color: NAVY, letterSpacing: 1 }}>
            ADD JOB FOR TODAY
          </h3>
          <button data-testid="close-form" onClick={onClose} className="tap"
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 8 }}>
            <X size={24} color={NAVY} />
          </button>
        </div>

        <FF label="Job Title *" testid="form-title">
          <input className="nn-input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Replace water heater" />
        </FF>
        <FF label="Customer Name" testid="form-customer">
          <input className="nn-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
        </FF>
        <FF label="Customer Phone" testid="form-phone">
          <input className="nn-input" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)}
            placeholder="(401) 555-0100" />
        </FF>
        <FF label="Address" testid="form-address">
          <input className="nn-input" value={address} onChange={(e) => setAddress(e.target.value)} />
        </FF>
        <FF label="Time" testid="form-time">
          <input className="nn-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </FF>
        <FF label="Estimated Value ($)" testid="form-value">
          <input className="nn-input" type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)}
            placeholder="0" />
        </FF>
        <FF label="Notes" testid="form-notes">
          <textarea className="nn-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FF>

        {err && <p data-testid="form-error" style={{ color: RED, fontSize: 14 }}>{err}</p>}

        <button data-testid="form-submit" onClick={submit} disabled={submitting}
          className="btn-gold tap" style={{ width: "100%", minHeight: 60, fontSize: 17, marginTop: 4 }}>
          {submitting ? "SAVING…" : "SAVE JOB"}
        </button>
      </motion.div>
    </motion.div>
  );
};

const FF = ({ label, testid, children }) => (
  <div data-testid={testid} style={{ marginBottom: 12 }}>
    <label className="nn-label" style={{ display: "block", marginBottom: 6, color: NAVY }}>
      {label}
    </label>
    {children}
  </div>
);

const ReviewWinForm = ({ contractorId, job, onClose, onCreated }) => {
  const [stars, setStars] = useState(5);
  const [customerName, setCustomerName] = useState(job.customer_name || "");
  const [quote, setQuote] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!quote.trim()) { setErr("Please paste the customer's review."); return; }
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/contractor/${contractorId}/review-wins`, {
        stars, customer_name: customerName, quote, source_url: sourceUrl || null,
      });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      data-testid="review-win-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, background: "rgba(10,22,40,0.65)",
        display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 100,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
        transition={{ type: "spring", stiffness: 280, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", width: "100%", maxWidth: 560,
          borderRadius: "20px 20px 0 0", padding: "20px 18px 28px", maxHeight: "92vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <p style={{ color: GREEN, fontFamily: "Bebas Neue, sans-serif",
                        letterSpacing: 3, margin: 0, fontSize: 11 }}>
              ⭐ REVIEW WON
            </p>
            <h3 style={{ margin: "4px 0 0", fontFamily: "Bebas Neue, sans-serif",
                         fontSize: 26, color: NAVY, letterSpacing: 1 }}>
              LOG IT — IT'LL MARKET YOU
            </h3>
          </div>
          <button data-testid="close-review-form" onClick={onClose} className="tap"
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 8 }}>
            <X size={24} color={NAVY} />
          </button>
        </div>

        <p style={{ color: GREY, fontSize: 13, marginTop: 0, marginBottom: 14 }}>
          Job: <strong style={{ color: NAVY }}>{job.title}</strong>
          {job.customer_name && <> · Customer: <strong style={{ color: NAVY }}>{job.customer_name}</strong></>}
          <br />
          After admin approval it shows on <strong>/testimonials</strong> and feeds the daily Hermes report.
        </p>

        <FF label="Stars">
          <div data-testid="stars-picker" style={{ display: "flex", gap: 6 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`star-${n}`}
                onClick={() => setStars(n)}
                type="button"
                className="tap"
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6, minWidth: 44, minHeight: 44 }}>
                <Star size={32} fill={n <= stars ? GOLD : "transparent"} color={n <= stars ? GOLD : "#cbd0db"} />
              </button>
            ))}
          </div>
        </FF>
        <FF label="Customer Name" testid="rw-form-customer">
          <input className="nn-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
        </FF>
        <FF label="The Review (paste it word-for-word) *" testid="rw-form-quote">
          <textarea className="nn-input" rows={5}
            value={quote} onChange={(e) => setQuote(e.target.value)}
            placeholder="Paste the exact text the customer wrote on Google, Yelp, or sent in a text." />
        </FF>
        <FF label="Source URL (Google review link, optional)" testid="rw-form-url">
          <input className="nn-input" value={sourceUrl} placeholder="https://maps.google.com/..."
            onChange={(e) => setSourceUrl(e.target.value)} />
        </FF>

        {err && <p data-testid="rw-form-error" style={{ color: RED, fontSize: 14 }}>{String(err)}</p>}

        <button data-testid="rw-form-submit" onClick={submit} disabled={busy}
          className="btn-gold tap" style={{ width: "100%", minHeight: 60, fontSize: 17, marginTop: 4 }}>
          {busy ? "SAVING…" : "LOG THE WIN — ADMIN WILL CURATE"}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default JobsToday;
